import React from 'react';
import { withFormik, Field, Form, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Modal } from 'react-bootstrap';
import { utcToZonedTime } from 'date-fns-tz';
import { FormikInput, FormikSelect, FormikTimePicker } from '../../../components';
import { delayMethod, isChileInSummerTime } from '../../../services/utils';
import timeTypes from '../../../components/Attendance/formOptions';

const AttendanceUpdateForm = props => {
  const {
    errors,
    touched,
    setFieldValue,
    handleModalClose,
    onHide,
    isSubmitting,
    setFieldTouched,
    values,
    attendance,
    branchOffices
  } = props;
  const { attendance: vAttendance } = values;
  const { timeType: vTimeType } = vAttendance;
  const { branchOfficeId: vBranchOfficeId } = vAttendance;

  return (
    <Form>
      <Row>
        <Col md={12} className="d-grid">
          <p>
            Si modificas la hora de marcación de una asistencia y el turno es nocturno, asegúrate de que la fecha sea la
            correcta.
          </p>
          <p>
            <strong>Ejemplo:</strong>
            <br />- <strong>Turno nocturno:</strong> 20-05-2025 <strong>(22:00)</strong> → 21-05-2025{' '}
            <strong>(05:00)</strong>
            <br />- Si registras la hora de salida a las <strong>04:30</strong>, la fecha debe ser{' '}
            <strong>21-05-2025</strong>, no el <strong>20-05-2025</strong>.
          </p>
          <p>Verifica siempre la fecha antes de guardar los cambios.</p>
          <span>
            <b>Trabajador:</b> {attendance.name}
          </span>
          <span>
            <b>Rut:</b> {attendance.nationalIdentification}
          </span>
          <span>
            <b>Fecha:</b> {attendance.date}
          </span>
          <br />
        </Col>
        <Col md={4}>
          <Field name="attendance[branchOfficeId]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                isDisabled={vAttendance.id !== null}
                label="Lugar de Prestación de Servicios"
                placeholder="Seleccionar L. P. de Servicios"
                options={branchOffices}
                defaultValue={vBranchOfficeId}
                onChange={data => {
                  setFieldValue(field.name, data ? data.value : '');

                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="attendance[parsedTime]">
            {({ field }) => (
              <FormikTimePicker
                {...field}
                abbr
                label="Hora"
                showTimeSelect
                selected={vAttendance?.time ? new Date(vAttendance?.time) : null}
                timeIntervals={1}
                timeCaption="Hora"
                timeFormat="p"
                dateFormat="Pp"
                onChange={date => {
                  setFieldValue(`attendance[time]`, date);
                  setFieldValue(field.name, date.toLocaleString('en-GB'));
                }}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={4}>
          <Field name="attendance[timeType]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Tipo de Hora"
                isDisabled={vAttendance.id === null}
                placeholder="Seleccionar Tipo de Hora"
                options={timeTypes}
                defaultValue={vTimeType}
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={12}>
          <Field name="attendance[comment]">
            {({ field }) => (
              <FormikInput
                {...field}
                label="Motivo"
                abbr
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Modal.Footer>
        <Button className="text-center" variant="outline-info" onClick={() => delayMethod(handleModalClose)}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" onClick={onHide} disabled={isSubmitting}>
          Guardar
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const setInitialValues = props => {
  const { attendance, timeType } = props;
  const { shift, date, timeZone } = attendance;
  const [day, month, year] = date.split('/');
  const formatDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const currentDate = isChileInSummerTime() ? new Date(`${formatDate}T03:00:00.000+00:00`) : new Date(`${formatDate}T04:00:00.000+00:00`)
  const time = attendance?.time ? new Date(attendance?.time) : currentDate
  const zonedDate = timeZone ? utcToZonedTime(time, timeZone) : utcToZonedTime(time, 'America/Santiago')

  return {
    attendance: {
      id: attendance.id,
      time: zonedDate,
      employeeId: attendance.employeeId,
      timeType: timeType ?? null,
      parsedTime: attendance.parsedTime ?? `${date}, 00:00:00`,
      shiftId: shift?.id,
      date,
      branchOfficeId: attendance.branchOfficeId,
      comment: ''
    }
  };
};

const validationSchema = Yup.object().shape({
  attendance: Yup.object().shape({
    time: Yup.string().required('Debes escoger la hora'),
    timeType: Yup.string().required('Debes escoger el tipo de hora'),
    branchOfficeId: Yup.string().required('Debes escoger el lugar de prestación de servicio'),
    comment: Yup.string()
      .required('Este campo es requerido')
      .min(5, 'El motivo debe tener al menos 5 caracteres')
      .max(255, 'El motivo debe tener máximo 255 caracteres')
  })
});

const handleSubmit = (values, { props, setSubmitting }) => {
  const { formRequest } = props;
  formRequest(values, setSubmitting);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: false,
  validateOnMount: false
})(AttendanceUpdateForm);
